/**
 * TODO: try to remake this component
 * because the code is so hidious
 */

import classNames from "classnames";
import { CSSProperties, HTMLAttributes, PropsWithChildren, ReactNode, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { TooltipContent, TooltipPosition, TooltipWrapperStyled } from "./index.styled";

export type PopupTooltipHoverProps = PropsWithChildren<HTMLAttributes<HTMLDivElement>> & {
  /**
   * The inside content of a tooltip
   */
  content: (() => ReactNode) | ReactNode;

  /**
   * Position of the tooltip from the hovered element
   * `top-left` sits above the element with the tooltip body extending to the left, so it is not clipped
   * by the right edge of the viewport.
   * @type {"left" | "right" | "top" | "bottom" | "left-top" | "right-top" | "top-left"}
   * @default {"top"}
   */
  position?: "left" | "right" | "top" | "bottom" | "left-top" | "right-top" | "top-left";

  /**
   * The color of the tooltip
   * @type {"white" | "black" | "blue" | "green" | "grey" | "red" | "yellow" | "dark-blue"}
   * @default {"white"}
   */
  color?: "white" | "black" | "blue" | "green" | "grey" | "red" | "yellow" | "dark-blue";

  /**
   * add additional class to the tooltip
   */
  tooltipClassName?: string;

  /**
   * Modifier for top position
   * @default {0}
   */
  topModifier?: number;

  /**
   * Margin size from tooltip to content
   * @default {14}
   */
  marginModifier?: number;
};

/**
 *
 * Improved Tooltip component that resolve issue not showing inside fixed max height scrolled div/container
 *
 * @namespace PopupTooltip.Hover
 * @example
 *  <PopupTooltip.Hover content={"Your content of tooltip here"} position={...}>
 *   // your hovered content here
 *  </PopupTooltip.Hover>
 *
 * @example
 * If somehow your tooltip needs to move, like it is inside a scrolled div/container
 * you need to wrap your scrolled div/container with PopupTooltip.Scrolled
 * <PopupTooltip.Scrolled>
 *  <YourScrolledContainer>
 *    ...
 *      ...
 *      <PopupTooltip.Hover content={"Your content of tooltip here"} position={...}>
 *       // your hovered element somewhere deep inside your scrolled component
 *      </PopupTooltip.Hover>
 *      ...
 *    ...
 *  </YourScrolledContainer>
 * </PopupTooltip.Scrolled>
 */
const TOP_LEFT_ARROW_INSET = 24; // px from the tooltip's right edge to its arrow, for position="top-left"

function Hover({
  children,
  position,
  content,
  className,
  tooltipClassName,
  color,
  topModifier,
  marginModifier = 14,
  ...rest
}: PopupTooltipHoverProps) {
  let mounted = true;
  const hideTooltipTimout = useRef<NodeJS.Timeout>();
  const hoverRef = useRef<HTMLDivElement>();
  const tooltipRef = useRef<HTMLDivElement>();
  const [isHoveringOnTooltip, setIsHoveringOnTooltip] = useState(false);
  const [isHoveringOnDiv, setIsHoveringOnDiv] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [animate, setAnimate] = useState(false);
  const [tooltipCords, setTooltipCords] = useState<CSSProperties>({
    left: 0,
    top: 0
  });
  const [tooltipArrowCords, setTooltipArrowCords] = useState<CSSProperties>({
    left: 0,
    top: 0,
    transform: ""
  });

  const MARGIN_FROM_TOOLTIP = marginModifier; // distance between tooltip and the hovered element (in pixel);
  const ADJUSTMENT_TO_ARROW = 6; // adjustment distance for arrow with the tooltip;
  const contentWrapperEl = document.querySelector(".ffb-tooltip-content-container");

  function onMouseEnter() {
    if (!mounted || !content) return;

    clearTimeout(hideTooltipTimout.current);

    setAnimate(true);
    setShowTooltip(true);
    setIsHoveringOnDiv(true);
    resetTooltipCoordinates();
  }

  function onMouseLeave() {
    if (!mounted) return;
    if (isHoveringOnTooltip) return;

    // set timeout in case user is only want to hover on the tooltip
    hideTooltipTimout.current = setTimeout(() => {
      setAnimate(false);
      setShowTooltip(false);
    }, 100);
  }

  function onScroll() {
    resetTooltipCoordinates(true);
  }

  function onDOMscroll() {
    resetTooltipCoordinates();
  }

  /**
   * Get the element height of the hovered element.
   * One of the purpose is for right/left tooltip, so the tooltip will appear at the first line.
   */
  function getElementLineHeight(element: HTMLDivElement) {
    let lineHeight = 0;
    try {
      const elLineHeight = window.getComputedStyle(element, null).getPropertyValue("line-height");
      lineHeight = parseInt(elLineHeight.replace("px", ""));
    } catch {
      lineHeight = 0;
    }
    return lineHeight;
  }

  function resetTooltipCoordinates(closeTooltip = false) {
    if (!mounted) return;

    const tooltipContainerEl = document.querySelector(".ffb-tooltip-content-container");
    const layoutRect =
      tooltipContainerEl?.nextElementSibling?.getBoundingClientRect() ||
      tooltipContainerEl?.previousElementSibling?.getBoundingClientRect();
    const hoveredRect = hoverRef.current?.getBoundingClientRect();
    const tooltipRect = tooltipRef.current?.getBoundingClientRect();

    if (!tooltipContainerEl || !hoveredRect || !tooltipRect) return;

    if (position === "left") {
      const left = hoveredRect.left - (tooltipRect.width + MARGIN_FROM_TOOLTIP + ADJUSTMENT_TO_ARROW);
      const top = hoveredRect.top - layoutRect.top - (tooltipRect.height / 2 - hoveredRect.height / 2);

      setTooltipArrowCords({
        left: tooltipRect.width - 2,
        top: tooltipRect.height / 2,
        transform: "translateX(-50%) rotate(-90deg)"
      });
      setTooltipCords({
        left,
        top
      });
      if (closeTooltip) onMouseLeave();
      return;
    }

    if (position === "left-top") {
      const lineHeight = getElementLineHeight(hoverRef.current);
      const left = hoveredRect.left - (tooltipRect.width + MARGIN_FROM_TOOLTIP + ADJUSTMENT_TO_ARROW);
      const top = hoveredRect.top - layoutRect.top - lineHeight / 1.5 + topModifier;

      setTooltipArrowCords({
        left: tooltipRect.width - 2,
        top: 24,
        transform: "translateX(-50%) rotate(-90deg)"
      });
      setTooltipCords({
        left,
        top
      });
      if (closeTooltip) onMouseLeave();
      return;
    }

    if (position === "right") {
      const left = hoveredRect.left + (hoveredRect.width + MARGIN_FROM_TOOLTIP + ADJUSTMENT_TO_ARROW);
      const top = hoveredRect.top - layoutRect.top - (tooltipRect.height / 2 - hoveredRect.height / 2);

      setTooltipArrowCords({
        left: -MARGIN_FROM_TOOLTIP + 6,
        top: tooltipRect.height / 2,
        transform: "rotate(90deg)"
      });
      setTooltipCords({
        left,
        top
      });
      if (closeTooltip) onMouseLeave();
      return;
    }

    if (position === "right-top") {
      const lineHeight = getElementLineHeight(hoverRef.current);
      const left = hoveredRect.left + (hoveredRect.width + MARGIN_FROM_TOOLTIP + ADJUSTMENT_TO_ARROW);
      const top = hoveredRect.top - layoutRect.top - lineHeight / 1.5 + topModifier;

      setTooltipArrowCords({
        left: -MARGIN_FROM_TOOLTIP + ADJUSTMENT_TO_ARROW,
        top: 24,
        transform: "rotate(90deg)"
      });
      setTooltipCords({
        left,
        top
      });
      if (closeTooltip) onMouseLeave();
      return;
    }

    if (position === "bottom") {
      const left = hoveredRect.left - (tooltipRect.width / 2 - hoveredRect.width / 2);
      const top =
        hoveredRect.bottom - (layoutRect.top - hoveredRect.height + MARGIN_FROM_TOOLTIP - ADJUSTMENT_TO_ARROW);

      setTooltipArrowCords({
        left: tooltipRect.width / 2,
        top: 0,
        transform: "translateX(-50%) rotate(-180deg)"
      });
      setTooltipCords({
        left,
        top
      });
      if (closeTooltip) onMouseLeave();
      return;
    }

    if (position === "top-left") {
      const left = hoveredRect.left + hoveredRect.width / 2 - (tooltipRect.width - TOP_LEFT_ARROW_INSET);
      const top = hoveredRect.top - layoutRect.top - tooltipRect.height - MARGIN_FROM_TOOLTIP;

      setTooltipArrowCords({
        left: tooltipRect.width - TOP_LEFT_ARROW_INSET,
        top: tooltipRect.height - 3,
        transform: "translateX(-50%) rotate(0deg)"
      });
      setTooltipCords({
        left,
        top
      });
      if (closeTooltip) onMouseLeave();
      return;
    }

    /**
     * @default position = "top"
     */
    const left = hoveredRect.left - (tooltipRect.width / 2 - hoveredRect.width / 2);
    const top = hoveredRect.top - layoutRect.top - tooltipRect.height - MARGIN_FROM_TOOLTIP;

    setTooltipArrowCords({
      left: tooltipRect.width / 2,
      top: tooltipRect.height - 3,
      transform: "translateX(-50%) rotate(0deg)"
    });
    setTooltipCords({
      left,
      top
    });
    if (closeTooltip) onMouseLeave();
  }

  useEffect(() => {
    if (isHoveringOnTooltip || isHoveringOnDiv) {
      clearTimeout(hideTooltipTimout.current);
    } else {
      onMouseLeave();
    }
  }, [isHoveringOnTooltip, isHoveringOnDiv]);

  useEffect(() => {
    const scrolledWrapper = hoverRef.current?.closest(".ffb-tooltip-scrolled-container")
      ?.firstElementChild as HTMLElement;

    hoverRef.current?.addEventListener("mouseenter", onMouseEnter);
    hoverRef.current?.addEventListener("mouseleave", onMouseLeave);
    scrolledWrapper?.addEventListener("scroll", onScroll);
    document.addEventListener("scroll", onDOMscroll);

    return () => {
      hoverRef.current?.removeEventListener("mouseenter", onMouseEnter);
      hoverRef.current?.removeEventListener("mouseleave", onMouseLeave);
      scrolledWrapper?.removeEventListener("scroll", onScroll);
      document.removeEventListener("scroll", onDOMscroll);
    };
  }, [showTooltip, tooltipRef.current]);

  useEffect(() => {
    () => {
      mounted = false;
    };
  }, []);
  return (
    <>
      <div
        data-testid="qa-popup-tooltip-children-container"
        className={classNames("u-inline-block", className)}
        ref={hoverRef}
        {...rest}
      >
        {children}
      </div>
      <ContentPortal container={contentWrapperEl} showTooltip={showTooltip}>
        <TooltipPosition>
          <TooltipContent
            data-testid="qa-popup-tooltip-content-container"
            ref={tooltipRef}
            className={classNames("u-p-3 u-shadow", tooltipClassName, animate ? "visible" : "hidden")}
            style={tooltipCords}
            onMouseEnter={() => {
              setIsHoveringOnTooltip(true);
              setIsHoveringOnDiv(false);
            }}
            onMouseLeave={() => setIsHoveringOnTooltip(false)}
            color={color}
          >
            {typeof content === "function" ? content() : content}
            <div className="tooltip-arrow" style={tooltipArrowCords} />
          </TooltipContent>
        </TooltipPosition>
      </ContentPortal>
    </>
  );
}

function TooltipWrapper() {
  return <TooltipWrapperStyled data-testid="qa-popup-tooltip-wrapper" className="ffb-tooltip-content-container" />;
}

/**
 *
 * Wrapper for the scrolled parent/grandparent component
 */
function Scrolled({ children }: PropsWithChildren<{}>) {
  return (
    <div data-testid="qa-popup-tooltip-scrolled-container" className="ffb-tooltip-scrolled-container">
      {children}
    </div>
  );
}

function ContentPortal({ children, container, showTooltip }: any) {
  if (!showTooltip || !container) return null;

  return createPortal(children, container);
}

function PopupTooltip(): null {
  return null;
}

Hover.defaultProps = {
  position: "top",
  color: "white",
  topModifier: 0
};

PopupTooltip.Scrolled = Scrolled;
PopupTooltip.Wrapper = TooltipWrapper;
PopupTooltip.Hover = Hover;

export default PopupTooltip;
